/* eslint-disable react/prop-types */
import React, { ButtonHTMLAttributes, forwardRef, useCallback, useEffect, useState } from 'react';
import { Tooltip } from '../Tooltip';
import { isInternalUrl } from '../Link/Link';
import { useHistory } from 'react-router-dom';
import { TopBarLocationState } from '../TopBar/TopBarProvider';
import { declareVariants } from '../../lib/variantMatch';
import { cn } from '../../lib/cn';

export type IconButtonProps = {
	// A single icon, or multiple if different states for active/inactive
	icon?: React.ReactNode | React.ReactNode[];
	size?: 'l' | 'm' | 's' | 'xs';
	active?: boolean;
	tooltip?: string | [string, string];
	badge?: string | number;
	badgeClassName?: string;
	href?: string;
	hrefState?: Record<string, unknown> & TopBarLocationState;
	onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>, active: boolean) => void;
	onToggle?: (active: boolean) => void;
	className?: string;
	children?: React.ReactNode;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>;

type RefType = HTMLButtonElement | HTMLAnchorElement;

export const IconButton = forwardRef<RefType, IconButtonProps>(({
	icon,
	size = 'l',
	active: initialActive = false,
	tooltip,
	badge,
	badgeClassName,
	href,
	hrefState,
	onClick,
	onToggle,
	className,
	children,
	...props
}, ref) => {
	const [active, setActive] = useState(initialActive);
	const tooltipText = Array.isArray(tooltip) ? tooltip[active ? 1 : 0] : tooltip;
	const history = useHistory();

	const variants = declareVariants({
		size,
		active,
	});

	useEffect(() => {
		setActive(initialActive);
	}, [initialActive]);

	const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		event.preventDefault();
		if (href) {
			if (!isInternalUrl(href)) {
				window.open(href, '_blank', 'noopener,noreferrer');
			} else {
				history.push(href, hrefState);
			}
		} else {
			onClick?.(event, !active);
			onToggle?.(!active);
		}
	}, [active, history, href, hrefState, onClick, onToggle]);

	const commonProps = {
		className: cn(
			'group relative inline-flex cursor-pointer items-center justify-center rounded-sm',
			'[&_svg]:fill-current', // SVG fill to text color
			'text-fg-primary',
			'hover:text-fg-primary hover:bg-bg-interactive-light-bg-active_hover',
			'active:not-disabled:text-fg-primary active:not-disabled:bg-bg-interactive-light-bg-active_pressing data-active:text-fg-primary data-active:bg-bg-interactive-light-bg-active_pressing',
			'disabled:text-fg-quaternary',
			variants({
				size: {
					l: 'size-[40px] min-w-[40px] min-h-[40px] [&_svg]:size-[24px]!',
					m: 'size-[36px] min-w-[36px] min-h-[36px] [&_svg]:size-[24px]!',
					s: 'size-[30px] min-w-[30px] min-h-[30px] [&_svg]:size-[18px]!',
					xs: 'size-[24px] min-w-[24px] min-h-[24px] [&_svg]:size-[16px]!',
				},
			}),
			className,
		),
		'data-active': active || undefined,
		onClick: handleClick,
		...props,
	};

	const content = (
		<>
			{Array.isArray(icon) ? (
				<>
					<div className={cn('absolute inset-0 flex items-center justify-center leading-none opacity-100 transition-opacity! duration-200 group-data-active:opacity-0')}>
						{icon[0]}
					</div>
					<div className={cn('absolute inset-0 flex items-center justify-center leading-none opacity-0 transition-opacity! duration-200 group-data-active:opacity-100')}>
						{icon[1]}
					</div>
				</>
			) : (
				<>
					{icon}
				</>
			)}
			{!!badge && (
				<span className={cn('badge absolute top-0 right-0 rounded-full bg-cherry-400 text-white text-xs font-bold size-4! flex items-center justify-center', badgeClassName)}>
					{badge}
				</span>
			)}
			{children}
		</>
	);

	return (
		<Tooltip title={tooltipText}>
			{href ? (
				<a
					ref={ref as React.Ref<HTMLAnchorElement>}
					href={href}
					target={isInternalUrl(href) ? '_self' : '_blank'}
					rel={isInternalUrl(href) ? undefined : 'noopener noreferrer'}
					{...commonProps}
				>
					{content}
				</a>
			) : (
				<button
					ref={ref as React.Ref<HTMLButtonElement>}
					type="button"
					{...commonProps}
				>
					{content}
				</button>
			)}
		</Tooltip>
	);
});
